import "./Header.css";
import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext"
import { getCloudinaryUrl } from "../utils/cloudinary";
import { CLOUDINARY_IMAGES } from "../constants/images";
import { TEMPLATE_CONFIG } from "../config/template.config";

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isScrollingToTop = useRef(false);
  
  // NEW: Mobile menu states
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const {t} = useLanguage();
  const { language, setSpecificLanguage } = useLanguage();
  const availableLanguages = TEMPLATE_CONFIG.site.availableLanguages;

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
        if (window.innerWidth > 768) {
            setIsMobileMenuOpen(false); // Close menu if resized to desktop
        }
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);


  // Scroll detection - only when not actively scrolling to top
  useEffect(() => {
    const handleScroll = () => {
      if (!isScrollingToTop.current) {
        setIsScrolled(window.scrollY > 50);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle smooth scroll to top
  const smoothScrollToTop = () => {
    isScrollingToTop.current = true;
    
    // Keep header in current state during scroll
    const currentScrolled = window.scrollY > 200;
    setIsScrolled(currentScrolled);
    
    // Start smooth scroll
    window.scrollTo({ top: 0, behavior: "smooth" });
    
    // Wait for scroll to complete (600ms is safe for smooth scroll)
    setTimeout(() => {
      isScrollingToTop.current = false;
      // Now update header based on actual position
      setIsScrolled(window.scrollY > 50);
    }, 600);
  };

  // Handle route changes
  useEffect(() => {
    isScrollingToTop.current = true;
    
    // Set header to big immediately
    setIsScrolled(false);
    
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: "smooth" });
    
    // After animation, re-enable scroll detection
    setTimeout(() => {
      isScrollingToTop.current = false;
      setIsScrolled(window.scrollY > 50);
    }, 800);
    
    // Close mobile menu on route change
    setIsMobileMenuOpen(false); 
  }, [location.pathname]);

   // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".lang-dropdown") && !e.target.closest(".mobile-menu-overlay")) setDropdownOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLanguageSelect = (lang) => {
      setSpecificLanguage(lang);
      setDropdownOpen(false);
      // If mobile, close the main menu after language switch
      if (isMobile) setIsMobileMenuOpen(false);
  }

  const handleNavLinkClick = (e, linkTo) => {
      if (location.pathname === linkTo) {
          e.preventDefault();
          smoothScrollToTop();
      }
      if (isMobile) {
          setIsMobileMenuOpen(false);
      }
  }

  return (
    <header className={`header ${isScrolled ? "scrolled" : ""}`}>
      <div className="header-container">
        <NavLink
          to="/"
          className="logo-group"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
              smoothScrollToTop();
            }
          }}
        >
          <img 
            src={getCloudinaryUrl(CLOUDINARY_IMAGES.other.logo, { 
                width: 300, 
                quality: 90,
                format: 'auto'
            })} 
            alt="Woodcarver Logo" 
            className="logo" 
          />
          <div className="artist-name">
            <span className="surname">{t.header.woodcarverSurname}</span>
            <span className="name">&nbsp;&nbsp;{t.header.woodcarverName}</span>
            <span className="profession">{t.header.woodcarverSpeciality}</span>
          </div>
        </NavLink>

        {/* REGULAR NAVIGATION (DESKTOP) */}
        <nav className="nav">
          {[
            { to: "/", label: t.header.main },
            { to: "/gallery", label: t.header.gallery },
            { to: "/about", label: t.header.about },
            { to: "/contact", label: t.header.contact },
          ].map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={(e) => handleNavLinkClick(e, link.to)}
              className={({ isActive }) =>
                isActive ? "nav-option active" : "nav-option"
              }
            >
              <span>{link.label}</span>
            </NavLink>
          ))}
        </nav>
        
        {/* LANGUAGE DROPDOWN (DESKTOP) */}
        <div className="lang-dropdown">
          <button
            className="lang-button"
            onClick={() => setDropdownOpen((prev) => !prev)}
          >
            {language.toUpperCase()}
            <span className={`arrow ${dropdownOpen ? "up" : "down"}`}></span>
          </button>
          {dropdownOpen && (
          <div className="lang-menu">
            {availableLanguages.filter(lang => lang !== language).map(lang => (
              <button
                key={lang}
                className="lang-option"
                onClick={() => handleLanguageSelect(lang)}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        )}
        </div>
        
        {/* NEW: MOBILE MENU TOGGLE BUTTON */}
        <button 
            className="menu-toggle-button"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav-menu"
        >
            <i className={isMobileMenuOpen ? "fa-solid fa-times" : "fa-solid fa-bars"}></i>
        </button>

      </div>

      {/* NEW: MOBILE MENU OVERLAY */}
      <div id="mobile-nav-menu" className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
          <nav className="mobile-nav-links">
              {[
                  { to: "/", label: t.header.main },
                  { to: "/gallery", label: t.header.gallery },
                  { to: "/about", label: t.header.about },
                  { to: "/contact", label: t.header.contact },
              ].map((link) => (
                  <NavLink
                      key={link.to}
                      to={link.to}
                      onClick={(e) => handleNavLinkClick(e, link.to)}
                      className={({ isActive }) =>
                          isActive ? "mobile-nav-option active" : "mobile-nav-option"
                      }
                  >
                      {link.label}
                  </NavLink>
              ))}
          </nav>

          <div className="mobile-lang-switcher">
              {availableLanguages.map(lang => (
                  <button
                      key={lang}
                      className={`mobile-lang-option ${language === lang ? 'active' : ''}`}
                      onClick={() => handleLanguageSelect(lang)}
                  >
                      {lang.toUpperCase()}
                  </button>
              ))}
          </div>
      </div>
       
    </header>
  );
}

export default Header;